import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Text,
  Tag,
  Flex,
  Box,
  Image,
  Button,
  Select,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import avatar from "../../../src/images/Avatar.png";
import { advertiserDetail, createChat, updateUserStatus } from "../../api";
import { useNavigate } from "react-router-dom";
import AuditLogPreview from "../AuditLogPreview";

const statusMeta = {
  ACTIVE: { label: "활성", color: "green" },
  INACTIVE: { label: "비활성", color: "gray" },
  BANNED: { label: "차단", color: "red" },
};

export function AdvertiserMemberModal({
  isOpen,
  onClose,
  type,
  onSuccess,
  accountId,
}) {
  const roleTag = type === "advertiser" ? "광고주" : "에이전시";
  const tagColor = type === "advertiser" ? "#BEE3F8" : "#CDE4FF";
  const tagTextColor = type === "advertiser" ? "#1E4E8C" : "#1E40AF";
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  const toast = useToast();

  const handleClickDM = async () => {
    try {
      const body = {
        targetProfileId: data.profileId,
        targetRole: "advertiser",
      };

      await createChat({ body });

      onClose();
      navigate(`/dm`);
    } catch (error) {
      console.error("채팅방 생성 실패:", error);
      alert("채팅방 생성에 실패했습니다. 다시 시도해주세요.");
    }
  };

  useEffect(() => {
    if (!accountId) return;
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountId]);

  const loadDetail = async () => {
    try {
      const res = await advertiserDetail(accountId);
      setData(res.data.data);
    } catch (e) {
      console.error("상세 조회 실패", e);
    }
  };

  const handleStatusChange = async (status) => {
    if (!data?.userId || !status || data.userStatus === status) return;
    const ok = window.confirm(
      `${data?.company?.organizationName || data?.displayName || "계정"} 상태를 ${
        statusMeta[status]?.label ?? status
      }(으)로 변경할까요?`
    );
    if (!ok) return;

    try {
      await updateUserStatus({ userId: data.userId, status });
      toast({ status: "success", title: "계정 상태가 변경되었습니다." });
      await loadDetail();
      onSuccess?.();
    } catch (e) {
      toast({
        status: "error",
        title: "계정 상태 변경 실패",
        description: e?.response?.data?.message ?? "잠시 후 다시 시도해주세요.",
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius="20px" p="10px">
        <ModalHeader fontSize="22px">멤버 관리</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="30px">
          <Box display="flex" flexDir="column">
            <Flex
              w="100%"
              alignItems="center"
              justify="space-between"
              mb="20px"
            >
              <Flex w="100%" gap="12px" align="center">
                <Image src={avatar} w="40px" h="40px" />
                <Text fontWeight="700" fontSize="20px">
                  {data?.company.organizationName || data?.displayName}
                </Text>
                <Tag
                  bg={tagColor}
                  color={tagTextColor}
                  fontWeight="700"
                  borderRadius="full"
                  px="10px"
                  py="4px"
                >
                  {roleTag}
                </Tag>
                <HStack spacing="6px" onClick={(e) => e.stopPropagation()}>
                  <Tag colorScheme={statusMeta[data?.userStatus]?.color ?? "gray"}>
                    {statusMeta[data?.userStatus]?.label ?? data?.userStatus ?? "-"}
                  </Tag>
                  <Select
                    size="xs"
                    w="82px"
                    value={data?.userStatus ?? "ACTIVE"}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    isDisabled={!data?.userId}
                  >
                    <option value="ACTIVE">활성</option>
                    <option value="INACTIVE">비활성</option>
                    <option value="BANNED">차단</option>
                  </Select>
                </HStack>
              </Flex>

              <Button
                w="80px"
                alignSelf="end"
                fontSize="16px"
                fontWeight={500}
                bg="#0054C0"
                color="white"
                border="1px solid #D1D4D9"
                borderRadius="4px"
                p="4px"
                onClick={handleClickDM}
              >
                DM
              </Button>
            </Flex>
            <Flex flexDir="row" gap="10px">
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  대표자명{" "}
                  <Text as="span" color="red.500">
                    (필수)
                  </Text>
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.company.representativeName ?? ""}
                </Flex>
              </Flex>
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  사업자등록번호{" "}
                  <Text as="span" color="red.500">
                    (필수)
                  </Text>
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.company.businessRegistrationNo ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDir="row" gap="10px" w="100%">
              <Flex flexDir="column" w="50%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  업종/산업{" "}
                  <Text as="span" color="red.500">
                    (필수)
                  </Text>
                </Text>

                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="flex-start"
                  align="center"
                  borderRadius="8px"
                  px="10px"
                  overflow="auto"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  css={{
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none", // IE, Edge
                    scrollbarWidth: "none", // Firefox
                  }}
                >
                  {data?.company?.businessTypes?.map((item, idx) => (
                    <Text key={idx}>{item}</Text>
                  ))}
                </Flex>
              </Flex>

              <Flex flexDir="column" w="50%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  주소{" "}
                  <Text as="span" color="red.500">
                    (필수)
                  </Text>
                </Text>

                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="flex-start"
                  align="center"
                  borderRadius="8px"
                  px="10px"
                  overflow="auto"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  css={{
                    "&::-webkit-scrollbar": { display: "none" },
                    msOverflowStyle: "none", // IE, Edge
                    scrollbarWidth: "none", // Firefox
                  }}
                >
                  {data?.company?.address ?? ""}
                </Flex>
              </Flex>
            </Flex>

            <Flex flexDir="row" gap="10px">
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  국가{" "}
                  <Text as="span" color="red.500">
                    (필수)
                  </Text>
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.company.country ?? ""}
                </Flex>
              </Flex>
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  담당자 이름{" "}
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.contact?.contactName ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDir="row" gap="10px">
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  부서{" "}
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.contact?.department ?? ""}
                </Flex>
              </Flex>
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  직책{" "}
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.contact?.jobTitle ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDir="row" gap="10px">
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  담당자 이메일{" "}
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.contact?.email ?? ""}
                </Flex>
              </Flex>
              <Flex flexDir="column" w="100%">
                <Text fontSize="14px" fontWeight="600" mb="6px">
                  담당자 연락처{" "}
                </Text>
                <Flex
                  w="100%"
                  bgColor="#F4F7FA"
                  mb="20px"
                  h="46px"
                  justify="center"
                  align="center"
                  borderRadius="8px"
                >
                  {data?.contact?.phone ?? ""}
                </Flex>
              </Flex>
            </Flex>
            <Flex flexDir="column" gap="8px">
              <Text fontSize="14px" fontWeight="600" mb="6px">
                카테고리
              </Text>
              <Box
                maxW="97%"
                overflowX="auto"
                whiteSpace="nowrap"
                css={{ "&::-webkit-scrollbar": { display: "none" } }}
              >
                <Flex
                  gap="6px"
                  border="1px solid #D1D4D9"
                  p="12px"
                  borderRadius="6px"
                >
                  {data?.industryCategories.map((category, idx) => (
                    <Box
                      key={category?.id}
                      px="8px"
                      py="2px"
                      bg="#E9F2FF"
                      color="#4B5563"
                      fontSize="12px"
                      fontWeight={500}
                      borderRadius="6px"
                      flexShrink={0}
                    >
                      {category?.nameKo ?? ""}
                    </Box>
                  ))}
                </Flex>
              </Box>
            </Flex>
            <AuditLogPreview targetType="USER" targetId={data?.userId} />
          </Box>
          <Flex justifyContent="center" mt="20px">
            <Button
              w="104px"
              h="44px"
              fontWeight={700}
              fontSize="14px"
              bgColor="#0054C0"
              color="white"
              borderRadius="12px"
              onClick={onClose}
            >
              확인
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
